import { cn } from '@/lib/utils';
import { motion, useReducedMotion } from 'framer-motion';
import { HugeiconsIcon } from '@hugeicons/react';
import { UserMultipleIcon, Settings02Icon, ChartHistogramIcon, SecurityCheckIcon } from '@hugeicons/core-free-icons';

const AXIS_CONFIG = {
  personas: {
    label: 'Valor para las Personas',
    icon: UserMultipleIcon,
    iconBg: 'bg-cyan-500',
    iconShadow: 'shadow-cyan-500/30',
    numberColor: 'text-cyan-600',
    barColor: 'bg-cyan-500',
  },
  operacion: {
    label: 'Valor para la Operación',
    icon: Settings02Icon,
    iconBg: 'bg-violet-500',
    iconShadow: 'shadow-violet-500/30',
    numberColor: 'text-violet-600',
    barColor: 'bg-violet-500',
  },
  productividad: {
    label: 'Productividad y Sostenibilidad',
    icon: ChartHistogramIcon,
    iconBg: 'bg-emerald-500',
    iconShadow: 'shadow-emerald-500/30',
    numberColor: 'text-emerald-600',
    barColor: 'bg-emerald-500',
  },
  confianza: {
    label: 'Confianza y Uso Responsable',
    icon: SecurityCheckIcon,
    iconBg: 'bg-blue-500',
    iconShadow: 'shadow-blue-500/30',
    numberColor: 'text-blue-600',
    barColor: 'bg-blue-500',
  },
};

export function AxisCard({ axis, value, projected, trend, confidence, className }) {
  const config = AXIS_CONFIG[axis];
  const shouldReduce = useReducedMotion();

  if (!config) return null;

  const Wrapper = shouldReduce ? 'div' : motion.div;
  const wrapperProps = shouldReduce ? {} : { whileHover: { y: -4 }, transition: { duration: 0.25, ease: 'easeOut' } };

  return (
    <Wrapper
      {...wrapperProps}
      className={cn(
        'relative rounded-2xl border border-border/50 bg-card p-6 transition-all duration-300 cursor-default overflow-hidden',
        'hover:shadow-[0_20px_60px_-15px_rgba(103,30,117,0.12)] hover:border-[#671E75]/25',
        className,
      )}
      role="article"
      aria-label={`Eje VIA: ${config.label}`}>
      <div className="relative">
        {/* Icon + label */}
        <div className="flex items-center gap-3 mb-5">
          <div
            className={cn(
              'h-10 w-10 rounded-full shadow-lg flex items-center justify-center shrink-0',
              config.iconBg,
              config.iconShadow,
            )}>
            <HugeiconsIcon icon={config.icon} size={18} className="text-white" aria-hidden="true" />
          </div>
          <p className="text-xs font-bold uppercase tracking-wider text-foreground">{config.label}</p>
        </div>

        {/* Value */}
        <div className="flex items-baseline gap-2">
          <span className={cn('font-display text-4xl font-extrabold tabular', config.numberColor)}>{value ?? '—'}</span>
          {projected && <span className="text-sm text-muted-foreground font-medium">/ {projected} proyectado</span>}
        </div>

        {/* Confidence + trend */}
        {(trend || confidence) && (
          <div className="flex items-center gap-3 mt-4 text-xs">
            {trend && (
              <span
                className={cn(
                  'flex items-center gap-1 font-semibold px-2 py-0.5 rounded-full',
                  trend > 0
                    ? 'text-emerald-700 bg-emerald-100'
                    : trend < 0
                      ? 'text-red-700 bg-red-100'
                      : 'text-gray-600 bg-gray-100',
                )}>
                {trend > 0 ? '↑' : trend < 0 ? '↓' : '→'} {Math.abs(trend)}%
              </span>
            )}
            {confidence && (
              <span className="px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium border border-border/50">
                Confianza: {confidence}
              </span>
            )}
          </div>
        )}

        {/* Accent bar at bottom */}
        <div className={cn('h-1 w-8 rounded-full mt-4', config.barColor)} aria-hidden="true" />
      </div>
    </Wrapper>
  );
}
